import { ArrowDown, Copy } from '@gravity-ui/icons'
import { Button, Table, Tooltip } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { reportsApi } from '@/api/reports.api'
import { formatPLN } from '@/utils/currency'
import { formatDate } from '@/utils/dates'

interface ReportTableRowProps {
  report: any;
  driverName: string;
  vehiclePlate: string;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onClone: (id: string) => void;
  onDelete: (id: string) => void;
}

export function ReportTableRow({ report: r, driverName, vehiclePlate, onView,  onClone }: ReportTableRowProps) {
  const { t } = useTranslation()
  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const blob = await reportsApi.generateReport(r.id)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', `${driverName}-${r.dateFrom}-${r.dateTo}.xls`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download report:', error)
    }
  }

  return (
    <Table.Row key={r.id} className="cursor-pointer" >
      <Table.Cell onClick={() => onView(r.id)}>
        <span className="font-semibold text-zinc-600 dark:text-zinc-400">{r.identifier}</span>
      </Table.Cell>
      <Table.Cell onClick={() => onView(r.id)}>
        <div className="flex flex-col">
          <span className="font-bold text-zinc-800 dark:text-zinc-200">{driverName}</span>
        </div>
      </Table.Cell>
      <Table.Cell onClick={() => onView(r.id)}>
        <div className="flex flex-col">
          <span className="font-medium text-zinc-700 dark:text-zinc-300">{vehiclePlate}</span>
        </div>
      </Table.Cell>
      <Table.Cell onClick={() => onView(r.id)}>
        <span className="whitespace-nowrap  font-medium">
          {formatDate(r.dateFrom)} – {formatDate(r.dateTo)}
        </span>
      </Table.Cell>
      <Table.Cell onClick={() => onView(r.id)}>
        <div className="flex flex-col">
          <span className="font-black tabular-nums text-zinc-500">{formatPLN(r.totalNetPLN)}</span>
          <span className="text-xs font-medium text-zinc-400 tabular-nums">{formatPLN(r.totalNetEUR)} EUR</span>
        </div>
      </Table.Cell>
      <Table.Cell onClick={() => onView(r.id)}>
        <div className="flex flex-col">
          <span className="font-black tabular-nums">{formatPLN(r.totalGrossPLN)}</span>
          <span className="text-xs font-medium text-zinc-400 tabular-nums">{formatPLN(r.totalGrossEUR)} EUR</span>
        </div>
      </Table.Cell>

      <Table.Cell onClick={() => onView(r.id)}>
        <div className="flex justify-end gap-2">
          <Tooltip delay={0}>
            <Button
              isIconOnly
              size="sm"
              variant="secondary"
              onClick={e => {
                e.stopPropagation()
                onClone(r.id)
              }}
            >
              <Copy className="size-4" />
            </Button>
            <Tooltip.Content>
              <p>{t('common.clone')}</p>
            </Tooltip.Content>
          </Tooltip>

          <Tooltip delay={0}>
            <Button
              isIconOnly
              size="sm"
              variant="secondary"
              onClick={e => {
                handleDownload(e)
              }}
            >
              <ArrowDown className="size-4" />
            </Button>
            <Tooltip.Content>
              <p>{t('common.download')}</p>
            </Tooltip.Content>
          </Tooltip>
        </div>
      </Table.Cell>
    </Table.Row>
  )
}
